import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { type SpaceCompactProps } from 'antd/es/space/Compact';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { $SpaceCompact } from '../../../../../ui/form/SpaceCompact.ts';
import { ComputeExpressionMinimalInput } from '../../../compute/input/ComputeExpressionMinimalInput.tsx';
import {
  Datatype,
  type FieldDatatype,
  isSameFieldDatatype,
} from '../../../datatype/Datatype.ts';
import { FieldDatatypeValueInput } from '../../../datatype/input/FieldDatatypeValueInput.tsx';
import { ExpressionType } from '../../../IExpression.ts';
import { type IReference } from '../../../references/IReference.ts';
import { ExpressionReferenceInput } from '../../../references/input/ExpressionReferenceInput.tsx';
import { configLogger } from '../../../utils/configLogger.ts';
import { AsyncExpressionInput } from '../../async/input/AsyncExpressionInput.tsx';
import { ExpressionRepository } from '../../ExpressionsRepository.ts';
import { ValueExpressionType } from '../../value/ValueExpression.type.ts';
import {
  AllAssignableExpressionTypes,
  type IAssignableExpression,
} from '../IAssignableExpression.ts';
import { AssignableExpressionTypeSelector } from './AssignableExpressionTypeSelector.tsx';

type AllowedExpressionType =
  | ExpressionType.Value
  | ExpressionType.Reference
  | ExpressionType.Function
  | ExpressionType.Async;

export interface IExpressionInputProps {
  /**
   * È il tipo di dato che avremo in **output**.
   */
  field: FieldDatatype;
  references: IReference[];
  name: string;
  size?: SpaceCompactProps['size'];
  block?: SpaceCompactProps['block'];
  /**
   * Se valorizzato, permette la selezione delle sole espressioni indicate.
   * Se undefined, permette la selezione di tutte le espressioni.
   */
  allowedTypes?: AllowedExpressionType[];

  /**
   * Permette di customizzare il filtro delle reference nel
   * ExpressionReferenceInput.
   */
  allowsReference?: (
    reference: IReference,
    fieldDataType?: FieldDatatype,
  ) => boolean;

  disabled?: boolean;
}

export type IAssignableExpressionAllowsReference =
  IExpressionInputProps['allowsReference'];

/**
 * Rappresenta un'espressione che può essere assegnata ad un campo per
 * un confronto o un calcolo.
 * Di base rappresenta un valore, ma può essere anche un riferimento ad un
 * altro campo o una funzione.
 */
// TODO Gestire meglio gli `allowedTypes` per gli array
export function AssignableExpressionInput(props: IExpressionInputProps) {
  const {
    field: fieldDatatype,
    references,
    name,
    allowedTypes,
    allowsReference,
    disabled,
  } = props;

  const { clearErrors, getValues } = useFormContext();
  const setValue = useFormSetValue();

  const linkValue = useWatch({ name }) as IAssignableExpression;

  const valueName = `${name}.value`;

  const linkType = linkValue?.type as IAssignableExpression['type'];

  // Quando il campo viene disabilitato, resetto il field.
  useEffect(() => {
    if (disabled === true && getValues(name)) {
      setValue(name, undefined, { shouldValidate: false });
    }
  }, [disabled, getValues, name, setValue]);

  /**
   * In caso si passi da una `SubconditionsGroup` a una `Assignable`, ad
   * esempio perchè siamo in un `ConditionArgumentInput`, potrebbe capitare
   * che il tipo (`linkType`) è uno non valido per questo Input (es.
   * `subconditionsGroup`). In questo caso, oppure se non è definito, dobbiamo
   * _resettare_ il campo, portandolo ad uno disponibile (`allowedTypes`).
   * Utilizziamo `Value` perché è sempre disponibile, oltre ad essere il
   * più semplice / comune.
   *
   * Nota: I vecchi widget generati in sviluppo potrebbero avere il campo `name`
   * a {}, rendendo impossibile la validazione e quindi la modifica del widget.
   */
  useEffect(() => {
    if (!linkType || !AllAssignableExpressionTypes.includes(linkType)) {
      const nextValue = ValueExpressionType.schema.cast({}, { assert: false });
      // biome-ignore format: <tbd>
      configLogger.debug(`[AssignableExpressionInput.reset]`, { linkType, linkValue, nextValue });
      setValue(name, nextValue, { shouldValidate: false });
    }
  }, [linkType, linkValue, setValue, name]);

  const computedAllowedTypes = useMemo<
    AllowedExpressionType[] | undefined
  >(() => {
    if (fieldDatatype.type === Datatype.Array) {
      return [ExpressionType.Value, ExpressionType.Reference];
    }

    return allowedTypes;
  }, [allowedTypes, fieldDatatype.type]);

  // Mostra una select (ExpressionTypeSelector) per scegliere il tipo di dato
  // da far inserire all'utente (valore, riferimento, funzione).
  return (
    <$SpaceCompact size={props.size || 'middle'} block={props.block}>
      <AssignableExpressionTypeSelector
        size={props.size || 'middle'}
        value={linkType}
        allowedTypes={computedAllowedTypes}
        onChange={nextType => {
          if (disabled) return;
          const expressionType = ExpressionRepository.get(nextType);
          setValue(name, expressionType.schema.getDefault(), {
            shouldValidate: false,
            shouldTouch: false,
          });
          clearErrors();
        }}
        disabled={disabled}
      />
      {linkType === ExpressionType.Value && (
        // È fondamentale la presenza di `key` per evitare che React Hook Form
        // consider questo input come lo stesso degli altri.
        <FieldDatatypeValueInput
          size={props.size || 'middle'}
          key="value"
          name={valueName}
          field={fieldDatatype}
          disabled={disabled}
        />
      )}
      {linkType === ExpressionType.Reference && (
        <ExpressionReferenceInput
          size={props.size || 'middle'}
          key="reference"
          name={valueName}
          width={props.block ? '100%' : undefined}
          references={references.filter(r =>
            allowsReference
              ? allowsReference(r, fieldDatatype)
              : isSameFieldDatatype(r.field, fieldDatatype),
          )}
          disabled={disabled}
        />
      )}
      {fieldDatatype.type !== Datatype.Array &&
        linkType === ExpressionType.Function && (
          // TODO Aggiungere tipo di dato in output.
          <ComputeExpressionMinimalInput
            key="function"
            size={props.size || 'middle'}
            name={valueName}
            references={references}
            disabled={disabled}
          />
        )}
      {fieldDatatype.type !== Datatype.Array &&
        linkType === ExpressionType.Async && (
          <AsyncExpressionInput
            key="async"
            size={props.size || 'middle'}
            name={valueName}
            references={references}
            disabled={disabled}
          />
        )}
    </$SpaceCompact>
  );
}
